import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class IngredientsService {
  private get base() { return `${this.tenant.apiBaseUrl}/v1/tenant`; }
  constructor(private http: HttpClient, private tenant: TenantService) {}

  list(params?: any): Observable<any> {
    return this.http.get(`${this.base}/ingredients`, { params });
  }
  get(id: number): Observable<any> {
    return this.http.get(`${this.base}/ingredients/${id}`);
  }
  create(data: any): Observable<any> {
    return this.http.post(`${this.base}/ingredients`, data);
  }
  update(id: number, data: any): Observable<any> {
    // v1.6.946 — Asana 1215959290787886. PHP/Laravel does NOT parse a
    // multipart/form-data body on PUT/PATCH, so an ingredient edit that carried
    // a `image` file OR an `image_url` field silently lost it — the image was
    // never persisted and reappeared blank on the next open (this also hit
    // manual uploads, not just Spoonacular URLs). When the body is FormData,
    // POST it with Laravel method-spoofing (`_method=PUT`) so the file/fields
    // actually arrive. Plain-object (JSON) updates keep using PUT.
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return this.http.post(`${this.base}/ingredients/${id}`, data);
    }
    return this.http.put(`${this.base}/ingredients/${id}`, data);
  }
  delete(id: number): Observable<any> {
    return this.http.delete(`${this.base}/ingredients/${id}`);
  }
  bulkInsert(formData: FormData): Observable<any> {
    return this.http.post(`${this.base}/ingredients/bulk-insert`, formData);
  }

  bulkDelete(ids: number[]): Observable<any> {
    return this.http.delete(`${this.base}/ingredients/bulk-destroy`, { body: { ids } });
  }

  /**
   * v1.6.811 — Create-or-update ingredients by case-insensitive name match.
   *
   * Retires the long-running "list → client-match → POST (race-prone 422)"
   * pattern that lived in ai-import.component.ts (and recipe-builder,
   * follow-up ship). The endpoint:
   *   • no match → creates and returns outcome `created`
   *   • match exists, not trashed → updates in place, outcome `updated`
   *   • match exists, trashed → restores + updates, outcome `restored`
   *
   * Match is server-side `LOWER(name) = LOWER(?)`; no client-side normalization
   * needed. Required-on-create fields (report_name, ingredient_category_id,
   * measurement_id, cost_per_unit) must be in each item for items that don't
   * match an existing record. On the update branch they're all optional.
   *
   * Image upload and nutrition_facts sync are out of scope for this endpoint;
   * the regular create() / update() above still handle those.
   *
   * Retires API Request Backlog item #1 (`POST /v1/tenant/ingredients/upsert-by-name`).
   * Retires the v1.6.786, v1.6.790, and v1.6.801 long-running threads.
   *
   * @param items  Array of `{name, report_name?, ingredient_category_id?,
   *                measurement_id?, cost_per_unit?, is_active?}` records.
   *                Per the spec a single record may be sent without `items`
   *                wrapping; we always send the array form for consistency.
   */
  upsertByName(items: any[]): Observable<any> {
    return this.http.post(`${this.base}/ingredients/upsert-by-name`, { items });
  }
}
